<script>
    import { themeStore, themes } from '../../stores/themeStore';
    import { onMount } from 'svelte';
    import { slide } from 'svelte/transition';
    
    let currentTheme = 'light';
    let showDropdown = false;
    
    const themeOptions = [
        { value: 'light', label: 'Light', icon: 'fa-sun' },
        { value: 'dark', label: 'Dark', icon: 'fa-moon' },
        { value: 'classic', label: 'Classic', icon: 'fa-pen-nib' }
    ];
    
    // Get the icon for the current theme
    function getCurrentThemeIcon(theme) {
        const themeOption = themeOptions.find(option => option.value === theme);
        return themeOption ? themeOption.icon : 'fa-sun';
    }
    
    onMount(() => {
        themeStore.init();
    });
    
    // Subscribe to theme changes
    $: themeStore.subscribe(theme => {
        currentTheme = theme;
    });
    
    function handleThemeChange(theme) {
        themeStore.setTheme(theme);
        showDropdown = false;
    }
    
    function handleClickOutside(event) {
        if (!event.target.closest('.theme-dropdown-container')) {
            showDropdown = false;
        }
    }
    
    function handleKeydown(e) {
        if (e.key === 'Escape') {
            showDropdown = false;
        }
    }
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleKeydown} />

<div class="theme-dropdown-container">
    <button
        class="toolbar-button theme-button"
        on:click={() => showDropdown = !showDropdown}
        title="Select theme"
        aria-expanded={showDropdown}
        aria-haspopup="true"
        data-testid="theme-button"
    >
        <i class="fas {getCurrentThemeIcon(currentTheme)}"></i>
        <i class="fas fa-chevron-down dropdown-arrow"></i>
    </button>
    
    {#if showDropdown}
        <div 
            class="theme-dropdown" 
            transition:slide|local
            role="menu"
            tabindex="-1"
        >
            {#each themeOptions as theme (theme.value)}
                <button 
                    class="menu-item"
                    class:active={currentTheme === theme.value}
                    on:click={() => handleThemeChange(theme.value)}
                    role="menuitem"
                    data-testid="theme-{theme.value}"
                >
                    <i class="fas {theme.icon}"></i>
                    <span>{theme.label}</span>
                    {#if currentTheme === theme.value}
                        <i class="fas fa-check menu-item-check"></i>
                    {/if}
                </button>
            {/each}
        </div>
    {/if}
</div>

<style>
.theme-dropdown-container {
    position: relative;
    display: inline-block;
}

.theme-button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    min-width: fit-content;
}

.dropdown-arrow {
    margin-left: 2px;
    font-size: 0.75em;
    transition: transform 0.2s ease;
}

.theme-button[aria-expanded="true"] .dropdown-arrow {
    transform: rotate(180deg);
}

.theme-dropdown {
    position: absolute;
    top: calc(100% + var(--spacing-2));
    left: 0;
    z-index: var(--z-index-dropdown);
    min-width: 180px;
    padding: var(--spacing-2);
    background-color: var(--color-bg-file-dropdown);
    border-radius: var(--border-radius-2xl);
    box-shadow: var(--shadow-dropdown);
}

.menu-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-radius: var(--border-radius-xl);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: var(--spacing-2);
}

.menu-item:hover {
    background-color: var(--color-bg-hover-light);
}

.menu-item i:first-child {
    width: var(--spacing-4);
}

.menu-item.active {
    color: var(--color-text-primary);
    background-color: var(--color-bg-hover-medium);
}

.menu-item-check {
    margin-left: auto;
    color: var(--color-menu-checkmark);
}

/* Dark mode adjustments */
:global([data-theme="dark"]) .theme-button {
    color: var(--color-text-primary);
}

/* Responsive design - keep icon always visible */
@media (width <= 1400px) {
    .theme-button {
        padding: 0 var(--spacing-2);
    }
}
</style>